/**
 * The vocabulary for a translatable string, shared by the client and the
 * scripts that feed the translation registry.
 *
 * A UI string is written ONCE, where it is shown, with its English beside its
 * key:
 *
 *   t("contactsEmptyTitle", "No contacts yet")
 *   t("contactsCount", "{count} contacts", { count })
 *
 * and, for text defined outside a component (a module-level list, a column
 * definition, a shared menu), as a descriptor resolved at render:
 *
 *   const TABS = [{ id: "all", label: msg("tasksTabAll", "All") }];
 *   …  t(tab.label)
 *
 * `npm run ui-strings` reads those call sites and writes every key with its
 * English to `shared/uiStrings.generated.json`, which is what the translation
 * routine translates. So the English lives in the component's own chunk, not in
 * the first-load bundle, and there is no second copy of it to drift.
 *
 * Keys are camelCase identifiers, unique across the app; the same key used twice
 * must carry the same English (the generator refuses otherwise). `{name}`
 * placeholders are kept verbatim by the translator and filled by `interpolate`.
 */

export type Msg = { readonly key: string; readonly en: string };

/** A translatable string defined outside a component. Resolve with `t(m)`. */
export function msg(key: string, en: string): Msg {
  return { key, en };
}

export function isMsg(value: unknown): value is Msg {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Msg).key === "string" &&
    typeof (value as Msg).en === "string"
  );
}

export type MsgVars = Record<string, string | number | null | undefined>;

/** Replace `{name}` placeholders. An unknown placeholder is left as written. */
export function interpolate(text: string, vars?: MsgVars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = vars[name];
    return v === undefined || v === null ? whole : String(v);
  });
}
